// Pictures of things to count (21 Oct 2026, Years 1 to 3): `base_ten`
// blocks, `counters` in groups, and an `array` in rows and columns. How many
// there are is never written or spoken: counting them is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export const COUNT_ITEMS = [
  'apple',
  'star',
  'counter',
  'block',
  'fish',
  'ball',
  'pencil',
  'flower',
] as const;

export type CountItem = (typeof COUNT_ITEMS)[number];

export type BaseTenSpec = {
  type: 'base_ten';
  thousands: number;
  hundreds: number;
  tens: number;
  ones: number;
};

export type CountersSpec = {
  type: 'counters';
  groups: { item: CountItem; count: number }[];
};

export type ArraySpec = {
  type: 'array';
  rows: number;
  columns: number;
  item: CountItem;
};

export type CountSpec = BaseTenSpec | CountersSpec | ArraySpec;

// More than this many pieces of base ten cannot be told apart on a phone.
const MAX_BLOCKS = 40;
// Year 1 counts to 120 (24 Oct 2026): 12 full groups of ten fit.
const MAX_GROUPS = 12;
const MAX_IN_GROUP = 20;
const MAX_COUNTERS = 120;
const MAX_SIDE = 10;

function isWholeIn(value: unknown, low: number, high: number): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= low &&
    value <= high
  );
}

function isItem(value: unknown): value is CountItem {
  return (COUNT_ITEMS as readonly unknown[]).includes(value);
}

export function readBaseTen(
  record: Record<string, unknown>,
): BaseTenSpec | null {
  const read = (key: string, high: number): number | null => {
    const value = record[key] ?? 0;
    return isWholeIn(value, 0, high) ? (value as number) : null;
  };
  const thousands = read('thousands', 9);
  // Up to 19 of a smaller block, for questions about regrouping.
  const hundreds = read('hundreds', 19);
  const tens = read('tens', 19);
  const ones = read('ones', 19);
  if (thousands === null || hundreds === null || tens === null) return null;
  if (ones === null) return null;
  const blocks = thousands + hundreds + tens + ones;
  if (blocks < 1 || blocks > MAX_BLOCKS) return null;
  return { type: 'base_ten', thousands, hundreds, tens, ones };
}

export function readCounters(
  record: Record<string, unknown>,
): CountersSpec | null {
  const { groups } = record;
  if (!Array.isArray(groups)) return null;
  if (groups.length < 1 || groups.length > MAX_GROUPS) return null;
  const read: CountersSpec['groups'] = [];
  for (const entry of groups as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const group = entry as Record<string, unknown>;
    if (!isItem(group.item)) return null;
    if (!isWholeIn(group.count, 1, MAX_IN_GROUP)) return null;
    read.push({ item: group.item, count: group.count as number });
  }
  const total = read.reduce((sum, group) => sum + group.count, 0);
  if (total > MAX_COUNTERS) return null;
  return { type: 'counters', groups: read };
}

export function readArray(record: Record<string, unknown>): ArraySpec | null {
  const { rows, columns } = record;
  const item = record.item ?? 'counter';
  if (!isWholeIn(rows, 1, MAX_SIDE) || !isWholeIn(columns, 1, MAX_SIDE)) {
    return null;
  }
  if (!isItem(item)) return null;
  return {
    type: 'array',
    rows: rows as number,
    columns: columns as number,
    item,
  };
}

const PLURAL: Record<CountItem, string> = {
  apple: 'apples',
  star: 'stars',
  counter: 'counters',
  block: 'blocks',
  fish: 'fish',
  ball: 'balls',
  pencil: 'pencils',
  flower: 'flowers',
};

function list(words: string[]): string {
  return words.length < 2
    ? (words[0] ?? '')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

// What a screen reader is told in place of the picture: what kind of things
// are shown, never how many.
export function describeCount(spec: CountSpec): string {
  if (spec.type === 'base_ten') {
    const kinds = (
      [
        ['thousands', spec.thousands],
        ['hundreds', spec.hundreds],
        ['tens', spec.tens],
        ['ones', spec.ones],
      ] as const
    )
      .filter(([, count]) => count > 0)
      .map(([name]) => name);
    return `Base-ten blocks: ${list(kinds)}.`;
  }
  if (spec.type === 'array') {
    return `An array of ${PLURAL[spec.item]} in rows and columns.`;
  }
  const things = [...new Set(spec.groups.map((group) => PLURAL[group.item]))];
  const what = list(things);
  return spec.groups.length > 1
    ? `Groups of ${what} to count.`
    : `${what.charAt(0).toUpperCase()}${what.slice(1)} to count.`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export type BaseTenKind = 'thousand' | 'hundred' | 'ten' | 'one';

export type BaseTenPiece = {
  kind: BaseTenKind;
  x: number;
  y: number;
};

export type BaseTenPicture = {
  pieces: BaseTenPiece[];
  // The side of one small cube; a ten is 1 by 10 of them, a hundred 10 by
  // 10, and a thousand a hundred drawn as the front of a cube.
  unit: number;
  depth: number;
  width: number;
  height: number;
};

export const BASE_TEN_UNIT = 8;
const CUBE_DEPTH = 12;
const GAP = 10;
const KIND_GAP = 18;
const MAX_WIDTH = 340;
const EDGE = 8;

type Box = {
  width: number;
  height: number;
  place: (x: number, y: number) => void;
};

// Lays boxes left to right, starting a new row when the next one would not
// fit on a phone.
function flow(boxes: { box: Box; gap: number }[]): {
  width: number;
  height: number;
} {
  let x = EDGE;
  let y = EDGE;
  let rowHeight = 0;
  let widest = 0;
  for (const { box, gap } of boxes) {
    const start = x === EDGE ? x : x + gap;
    if (start !== EDGE && start + box.width > MAX_WIDTH - EDGE) {
      x = EDGE;
      y += rowHeight + GAP;
      rowHeight = 0;
    } else {
      x = start;
    }
    box.place(x, y);
    x += box.width;
    rowHeight = Math.max(rowHeight, box.height);
    widest = Math.max(widest, x);
  }
  return { width: widest + EDGE, height: y + rowHeight + EDGE };
}

export function baseTenPicture(spec: BaseTenSpec): BaseTenPicture {
  const unit = BASE_TEN_UNIT;
  const side = unit * 10;
  const pieces: BaseTenPiece[] = [];
  const boxes: { box: Box; gap: number }[] = [];
  let first = true;
  const add = (
    kind: BaseTenKind,
    count: number,
    width: number,
    height: number,
  ) => {
    for (let index = 0; index < count; index += 1) {
      boxes.push({
        gap: index === 0 && !first ? KIND_GAP : GAP,
        box: {
          width,
          height,
          place: (x, y) => pieces.push({ kind, x, y }),
        },
      });
    }
    if (count > 0) first = false;
  };
  add('thousand', spec.thousands, side + CUBE_DEPTH, side + CUBE_DEPTH);
  add('hundred', spec.hundreds, side, side);
  add('ten', spec.tens, unit, side);
  // The ones sit in one block, in rows of five so they are easy to count.
  if (spec.ones > 0) {
    const across = Math.min(spec.ones, 5);
    const down = Math.ceil(spec.ones / 5);
    const step = unit + 3;
    boxes.push({
      gap: spec.thousands + spec.hundreds + spec.tens > 0 ? KIND_GAP : GAP,
      box: {
        width: across * step - 3,
        height: down * step - 3,
        place: (x, y) => {
          for (let index = 0; index < spec.ones; index += 1) {
            pieces.push({
              kind: 'one',
              x: x + (index % 5) * step,
              y: y + Math.floor(index / 5) * step,
            });
          }
        },
      },
    });
  }
  const size = flow(boxes);
  return { pieces, unit, depth: CUBE_DEPTH, ...size };
}

export type CountersPicture = {
  groups: {
    x: number;
    y: number;
    width: number;
    height: number;
    items: { item: CountItem; x: number; y: number }[];
  }[];
  // The width and height of one thing.
  size: number;
  width: number;
  height: number;
};

export const COUNT_ITEM_SIZE = 26;
const ITEM_GAP = 6;
const PAD = 10;
const PER_ROW = 5;

// A big collection (Year 1 counts to 120) is drawn smaller, so two groups of
// ten sit side by side and the whole collection can be seen at once rather
// than in a column a screen and a half long.
const BIG_COLLECTION = 60;
const SMALL_ITEM_SIZE = 20;
const SMALL_ITEM_GAP = 4;
const SMALL_PAD = 7;

function itemsInRows(
  item: CountItem,
  count: number,
  perRow: number,
  left: number,
  top: number,
  step = COUNT_ITEM_SIZE + ITEM_GAP,
) {
  return Array.from({ length: count }, (_, index) => ({
    item,
    x: left + (index % perRow) * step,
    y: top + Math.floor(index / perRow) * step,
  }));
}

export function countersPicture(spec: CountersSpec): CountersPicture {
  const total = spec.groups.reduce((sum, group) => sum + group.count, 0);
  const small = total > BIG_COLLECTION;
  const itemSize = small ? SMALL_ITEM_SIZE : COUNT_ITEM_SIZE;
  const gap = small ? SMALL_ITEM_GAP : ITEM_GAP;
  const pad = small ? SMALL_PAD : PAD;
  const step = itemSize + gap;
  const groups: CountersPicture['groups'] = [];
  const size = flow(
    spec.groups.map((group) => {
      const across = Math.min(group.count, PER_ROW);
      const down = Math.ceil(group.count / PER_ROW);
      const width = across * step - gap + pad * 2;
      const height = down * step - gap + pad * 2;
      return {
        gap: GAP,
        box: {
          width,
          height,
          place: (x: number, y: number) =>
            groups.push({
              x,
              y,
              width,
              height,
              items: itemsInRows(
                group.item,
                group.count,
                PER_ROW,
                x + pad,
                y + pad,
                step,
              ),
            }),
        },
      };
    }),
  );
  return { groups, size: itemSize, ...size };
}

export type ArrayPicture = {
  items: { item: CountItem; x: number; y: number }[];
  size: number;
  width: number;
  height: number;
};

export function arrayPicture(spec: ArraySpec): ArrayPicture {
  const step = COUNT_ITEM_SIZE + ITEM_GAP;
  return {
    items: itemsInRows(
      spec.item,
      spec.rows * spec.columns,
      spec.columns,
      EDGE,
      EDGE,
    ),
    size: COUNT_ITEM_SIZE,
    width: spec.columns * step - ITEM_GAP + EDGE * 2,
    height: spec.rows * step - ITEM_GAP + EDGE * 2,
  };
}
